<template>
    <v-container grid-list-xs>
        <v-sheet class="pa-5" elevation="3">
            <Form>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <h4 class="primary-color">{{ $t("terms.titleHeading") }}</h4>
                        </v-col>
                    </v-row>

                    <v-row class="pa-0 mx-0">
                        <v-col class="pa-0 mx-0">
                            <div class="tc-container">
                                <PDFViewer v-if="pdfUrl" :pdf-url="pdfUrl" />
                            </div>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <div class="body-1">
                                I ask Synchrony Bank ("SYNCB") to issue me a CareCredit Credit Card (the "Card") and I
                                agree:
                            </div>
                            <ul class="mb-5 grey-darken-2">
                                <li>To the CareCredit Credit Card agreement ("Agreement").</li>
                                <li>
                                    I am providing the information in this application to SYNCB, CareCredit LLC, and
                                    providers that accept the Card and program sponsors (and their respective
                                    affiliates), and I consent to SYNCB's providing information about me to CareCredit
                                    LLC, providers that accept the Card and program sponsors (and their respective
                                    affiliates) for their own business purposes.
                                </li>
                                <li>
                                    SYNCB may obtain credit reports and other information, including employment and
                                    income, about me to evaluate my application and for other purposes.
                                </li>
                                <li>
                                    SYNCB, and any other owner or servicer of my account, may contact me about my
                                    account, including through text messages, automatic telephone dialing systems and/or
                                    artificial or prerecorded voice calls for informational, servicing or collection
                                    related communications, as provided in the Address/Phone Change and Consent To
                                    Communications provisions of the Agreement. I also agree to update my contact
                                    information.
                                </li>
                                <li>
                                    The Agreement will govern my account and includes:
                                    <span class="font-weight-bold">
                                        (1) a resolving a dispute with arbitration provision that limits my rights
                                        unless: (a) I reject the provision by following the provision's instructions or
                                        (b) I am covered by the Notice for Active Duty Military Members and their
                                        Dependents set forth in the Agreement; and
                                    </span>
                                    (2) makes each applicant responsible for paying the entire amount of credit
                                    extended.
                                </li>
                                <li>
                                    <b>
                                        Authorization for the Social Security Administration to Disclose Your Social
                                        Security Number Verification.
                                    </b>
                                    I authorize the Social Security Administration (SSA) to verify and disclose to SYNCB
                                    through SentiLink Verification Services Corp, SYNCB’s service provider, for the
                                    purpose of this transaction whether the name, Social Security Number (SSN) and date
                                    of birth I have submitted matches information in SSA records. My consent is for a
                                    one-time validation within the next 90 days.
                                </li>
                            </ul>
                            <p class="font-weight-bold">
                                Federal law requires SYNCB to obtain, verify and record information that identifies you
                                when you open an account. SYNCB will use your name, address, date of birth, and other
                                information for this purpose.
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>

                <div v-if="!jointEnabled">
                    <v-row justify="center">
                        <v-col class="pa-0 mx-1 mb-0">
                            <CheckboxField id="printed-terms-checkbox" v-model="receivedPrintedTerms">
                                {{
                                    $t("terms.print-checkbox", {
                                        applicantFirstName: applicant.primary.firstName,
                                        applicantLastName: applicant.primary.lastName,
                                    })
                                }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row justify="center" class="mb-2 mt-0">
                        <v-col class="pa-0 mx-1">
                            <CheckboxField
                                id="lender-terms-checkbox"
                                v-model="agreeWithLenderTerms"
                                :disabled="!receivedPrintedTerms"
                            >
                                {{
                                    $t("terms.signature-checkbox", {
                                        applicantFirstName: applicant.primary.firstName,
                                        applicantLastName: applicant.primary.lastName,
                                    })
                                }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <p>
                                I, {{ `${applicant.primary.firstName} ${applicant.primary.lastName}` }}, by selecting
                                "Agree & Complete" acknowledge that I am applying for a CareCredit credit card. I
                                acknowledge that this is my electronic signature and I have received a written copy of
                                the CareCredit Credit Card Account Agreement with a Rev number of 050124 (located in the
                                lower left corner of the paper application) and agree to the credit terms and
                                application disclosures, including the consent for the SSA to disclose my SSN
                                Verification to SYNCB, set forth above.
                            </p>
                        </v-col>
                    </v-row>

                    <v-row v-if="showCaliforniaDisclaimer">
                        <v-col>
                            <p>
                                <strong>{{ $t("terms.california-disclosure") }}</strong>
                            </p>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <SignaturePad
                                v-model="application.signatureData"
                                :disabled="!receivedPrintedTerms"
                                @signature-valid="valid => (validSignature = valid)"
                            />
                        </v-col>
                    </v-row>
                </div>

                <div v-else>
                    <h5 class="font-size-xl">
                        To be filled out by {{ `${applicant.primary.firstName} ${applicant.primary.lastName}` }}
                    </h5>
                    <v-row>
                        <v-col class="pa-0 mx-1">
                            <CheckboxField id="secondary-printed-terms-checkbox" v-model="receivedPrintedTerms">
                                {{ $t("terms.print-checkbox") }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row justify="center" class="mb-2">
                        <v-col class="pa-0 mx-1">
                            <CheckboxField id="secondary-lender-terms-checkbox" v-model="agreeWithLenderTerms">
                                {{ $t("terms.signature-checkbox") }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <p>
                        I, {{ `${applicant.primary.firstName} ${applicant.primary.lastName}` }}, by selecting "Agree &
                        Complete Application" acknowledge that I am applying for a CareCredit credit card. I acknowledge
                        that this is my electronic signature and I have received a written copy of the CareCredit Credit
                        Card Account Agreement with a Rev number of 050124 (located in the lower left corner of the
                        paper application) and agree to the credit terms and application disclosures, including the
                        consent for the SSA to disclose my SSN Verification to SYNCB, set forth above.
                    </p>

                    <v-row v-if="showCaliforniaDisclaimer">
                        <v-col>
                            <p>
                                <strong>{{ $t("terms.california-disclosure") }}</strong>
                            </p>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <SignaturePad
                                v-model="application.signatureData"
                                class="mt-5"
                                :disabled="!receivedPrintedTerms"
                                @signature-valid="valid => (validSignature = valid)"
                            />
                        </v-col>
                    </v-row>
                    <h5 class="font-size-xl">
                        To be filled out by
                        {{ `${applicant.joint.firstName} ${applicant.joint.lastName}` }}
                    </h5>
                    <v-row>
                        <v-col class="pa-0 mx-1">
                            <CheckboxField id="joint-printed-terms-checkbox" v-model="jointReceivedPrintedTerms">
                                {{ $t("terms.print-checkbox") }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row justify="center" class="mb-2">
                        <v-col class="pa-0 mx-1">
                            <CheckboxField id="joint-lender-terms-checkbox" v-model="jointAgreeWithLenderTerms">
                                {{ $t("terms.signature-checkbox") }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <p>
                        I, {{ `${applicant.joint.firstName} ${applicant.joint.lastName}` }}, by selecting "Agree &
                        Complete" acknowledge that I am applying for a CareCredit credit card. I acknowledge that this
                        is my electronic signature and I have received a written copy of the CareCredit Credit Card
                        Account Agreement with a Rev number of 050124 (located in the lower left corner of the paper
                        application) and agree to the credit terms and application disclosures, including the consent
                        for the SSA to disclose my SSN Verification to SYNCB, set forth above.
                    </p>

                    <v-row v-if="showCaliforniaDisclaimer">
                        <v-col>
                            <p>
                                <strong>{{ $t("terms.california-disclosure") }}</strong>
                            </p>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <SignaturePad
                                v-model="application.jointSignatureData"
                                class="mt-5"
                                :disabled="!receivedPrintedTerms"
                                @signature-valid="valid => (jointValidSignature = valid)"
                            />
                        </v-col>
                    </v-row>
                </div>

                <v-card-actions class="pb-12 px-0">
                    <v-row justify="space-between">
                        <v-col>
                            <BackButton class="mr-4" color="primary" rounded>
                                {{ $t("action.back") }}
                            </BackButton>
                        </v-col>
                        <v-col class="d-flex justify-end">
                            <SubmitButton
                                rounded
                                class="secondary submit-button"
                                :disabled="isSubmitButtonDisabled"
                                @click="next"
                            >
                                {{ $t("action.agreeAndCompleteApplication") }}
                            </SubmitButton>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </Form>
        </v-sheet>
    </v-container>
</template>

<script>
    import { applicationState, sessionState } from "@/state"
    import ApplyMode from "@/model/apply/ApplyMode"
    import StipulationViewMixin from "@/components/stipulation/StipulationViewMixin.vue"
    import StipulationType from "@/model/flow/StipulationType"
    import { mapGetters, mapState } from "vuex"
    import { isEntityInCalifornia } from "@/util/entity"
    import { applyService, googleAnalyticsService, logService } from "@/service"
    import GoogleAnalyticEvents from "@/model/google/GoogleAnalyticEvents"
    import PDFViewer from "@/components/terms/PDFViewer.vue"

    export default {
        components: { PDFViewer },
        mixins: [StipulationViewMixin],

        data() {
            return {
                agreeWithLenderTerms: false,
                applicant: applicationState.application || {},
                applyMode: applicationState.applyMode,
                jointAgreeWithLenderTerms: false,
                jointReceivedPrintedTerms: false,
                jointValidSignature: false,
                receivedPrintedTerms: false,
                validSignature: false,
                pdfUrl: "",
            }
        },

        computed: {
            ...mapState("application", {
                application: state => state.data,
            }),

            ...mapGetters(["applicantInfo"]),
            jointEnabled() {
                return applicationState.application.jointEnabled
            },

            jointApplication() {
                return [this.validSignature, this.jointValidSignature]
            },

            showCaliforniaDisclaimer() {
                return isEntityInCalifornia()
            },

            isSubmitButtonDisabled() {
                return (
                    !this.receivedPrintedTerms ||
                    !this.agreeWithLenderTerms ||
                    !this.validSignature ||
                    (this.jointEnabled && !this.jointAgreeWithLenderTerms) ||
                    (this.jointEnabled && !this.jointReceivedPrintedTerms) ||
                    (this.jointEnabled && !this.jointValidSignature) ||
                    this.isStipulationPending
                )
            },
        },

        async created() {
            const documentResponse = await applyService.getDocuments(sessionState.providerKey)
            const url = documentResponse.documents[documentResponse.documents.length - 1]?.uri || ""
            if (!url) {
                logService.info("Unable to retrieve terms document")
                await this.$router.push(`/providers/${sessionState.providerKey}/error`)
            }
            this.pdfUrl = url
        },

        methods: {
            getStipulationType() {
                return StipulationType.PRINT_DOCUMENT
            },

            next() {
                if (this.applyMode === ApplyMode.FULL) {
                    googleAnalyticsService.buttonEvent(
                        GoogleAnalyticEvents.FULL_APPLICATION_SUBMITTED,
                        "Synchrony Full Application Submitted"
                    )
                    this.$router.push(`/providers/${sessionState.providerKey}/fullApplicationSubmit`)
                } else {
                    googleAnalyticsService.buttonEvent(
                        GoogleAnalyticEvents.ACCEPT_OFFER_SUBMITTED,
                        "Synchrony Accept Offer Submitted"
                    )
                    this.$router.push(`/providers/${sessionState.providerKey}/acceptOfferSubmit`)
                }
            },
        },
    }
</script>

<style lang="scss">
    h5 {
        color: $grey-darken-2;
    }

    @media #{map-get($display-breakpoints, "sm-and-down")} {
        li {
            padding-left: 0 !important;
        }
    }

    .tc-container {
        height: 60vh;
        overflow-y: scroll;
    }

    .font-size-xl {
        font-size: to_rem(18);
    }

    .submit-button {
        height: fit-content !important;
        max-width: 140px;
        white-space: normal;
        display: block;

        @media #{map-get($display-breakpoints, "sm-and-up")} {
            display: inline-flex;
            white-space: nowrap;
            width: auto;
            max-width: fit-content;
            height: auto;
        }
    }
</style>
